import { createFileRoute, useParams } from '@tanstack/react-router'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import clsx from 'clsx'
import { useState } from 'react'
import { TrashIcon } from 'lucide-react'

import { RouterOutputs, useTRPC } from '@/trpc'
import Spinner from '@tandem/ui/Spinner'
import CheckmarkIcon from '@tandem/ui/icons/checkmark.svg?react'
import EllipsisIcon from '@tandem/ui/icons/ellipsis.svg?react'
import XMarkIcon from '@tandem/ui/icons/xmark.svg?react'

import $table from '@tandem/ui/table.css'
import $card from '@tandem/ui/card.css'
import $type from '@tandem/ui/type.css'
import $layout from '@tandem/ui/layout.css'
import $modal from '@tandem/ui/Modal/styles.css'
import $dropdownSelect from '@tandem/ui/DropdownSelect/styles.css'
import CompanyDetailsForm from '@/admin/CompanyDetailsForm'

import $ from './-CompanyDetails.css'
import EditUserForm from '@/admin/EditUserForm'
import Dropdown from '@tandem/ui/Dropdown'
import AddMemberForm from '@/admin/AddMemberForm'
import Modal from '@tandem/ui/Modal'

type CompanyDetails = RouterOutputs['company']['get']

export const Route = createFileRoute('/(admin)/companies/$id')({
  component: CompanyDetailsPage
})

function CompanyDetailsPage() {
  let { id } = useParams({ from: '/(admin)/companies/$id' })
  let trpc = useTRPC()
  let { data: company, isLoading, error } = useQuery(trpc.company.get.queryOptions(Number(id)))
  
  let [editingMemberId, setEditingMemberId] = useState<number | null>(null)
  let [isAddingMember, setIsAddingMember] = useState(false)

  if (isLoading) {
    return (
      <div class={$.splash}>
        <Spinner class={$.spinner} />
      </div>
    )
  }

  if (error || !company) {
    return (
      <div class={$.container}>
        <p class={clsx($type.muted, $type.error)}>Не удалось загрузить информацию о компании</p>
      </div>
    )
  }

  return (
    <div
      class={clsx('container', $.container)}
      style={{
        display: 'flex',
        flexDirection: 'column',
        width: 'fit-content'
      }}
    >
      <div class={clsx($type.breadcrumb, $type.small)}>
        <a href='/companies' class={$type.underline}>
          Компании
        </a>
        <span>/</span>
        <b>{company.name}</b>
      </div>

      <h2 class={clsx(company.isSystem && $.system)}>{company.name}</h2>

      <div style={{ display: 'flex', flexDirection: 'row', gap: '1rem' }}>
        <div>
          <h4 style={{ textAlign: 'center' }}>Общая информация</h4>
          <div class={$card.rect}>
            <CompanyDetailsForm company={company} />
          </div>
        </div>

        <div>
          <h4 style={{ textAlign: 'center' }}>Представители компании</h4>

          <div class={$table.scroller}>
            <table class={clsx($table.table)}>
              <thead>
                <tr>
                  <th>Имя</th>
                  <th>Контакты</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {company.members.map((member) => (
                  <MemberRow
                    key={member.id}
                    member={member}
                    companyId={company.id}
                    selected={member.id === editingMemberId}
                    onEdit={() => setEditingMemberId(member.id)}
                  />
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td colSpan={100}>
                    {isAddingMember ?
                      <AddMemberForm company={company} onCancel={() => setIsAddingMember(false)} onSuccess={() => {}} />
                    : <button
                        onClick={() => setIsAddingMember(true)}
                        class={clsx('plain', $type.underline, $type.muted, $type.small)}
                      >
                        Добавить представителя
                      </button>
                    }
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      </div>
      
      
      <Modal open={!!editingMemberId} onClose={() => setEditingMemberId(null)}>
        <div class={$modal.header}>
          <h3>Редактировать пользователя</h3>
          <button class={clsx('plain', $modal.close)} onClick={() => setEditingMemberId(null)} aria-label='Закрыть'>
            <XMarkIcon />
          </button>
        </div>          
        {editingMemberId != null && <EditUserForm user={{ id: editingMemberId }} />}
      </Modal>
      
    </div>
  )
}

function MemberRow({
  member,
  companyId,
  selected,
  onEdit
}: {
  member: CompanyDetails['members'][number]
  companyId: CompanyDetails['id']
  onEdit: () => void
  selected?: boolean
}) {
  let trpc = useTRPC()
  let queryClient = useQueryClient()

  let removeMemberMutation = useMutation(
    trpc.company.removeMember.mutationOptions({
      onSuccess() {
        queryClient.invalidateQueries(trpc.company.get.queryFilter(Number(companyId)))
      }
    })
  )

  return (
    <tr class={clsx({ [$table.active]: selected })}>
      <td>
        <button class='plain link' onClick={onEdit}>
          {member.first_name} {member.last_name}
        </button>
      </td>
      <td>
        {member.phones.map((phone) => (
          <div>{phone.number}</div>
        ))}
        {member.emails.map((email) => (
          <div class={$layout.withIcon}>
            {email.is_verified && <CheckmarkIcon class={clsx($layout.icon, $type.small)} title='Адрес подтвержден.' />}
            {email.address}
          </div>
        ))}
      </td>
      <td>
        <Dropdown
          trigger={
            <button class={clsx('plain', $type.muted)} title='Действия'>
              <EllipsisIcon style={{ width: '1em' }} />
            </button>
          }
        >
          <div class={$dropdownSelect.menu}>
            <button
              class={clsx('ghost', $dropdownSelect.option)}
              onClick={() =>
                removeMemberMutation.mutate({
                  company_id: companyId,
                  user_id: member.id
                })
              }
            >
              <TrashIcon />
              Исключить из компании
            </button>
          </div>
        </Dropdown>
      </td>
    </tr>
  )
}
